import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';

const BACKEND_URL = API_BASE_URL;

/**
 * Create a new ride request
 */
export const createRide = async (rideData) => {
    try {
        const response = await axios.post(
            `${BACKEND_URL}/api/rides/create`,
            rideData
        );

        return {
            success: true,
            data: response.data.ride,
            message: response.data.message
        };
    } catch (error) {
        console.error('Create ride error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || 'Failed to create ride'
        };
    }
};

/**
 * Cancel a ride (used for scheduled rides before dispatch — fee is 0 with no driver)
 */
export const cancelRide = async (rideId, cancelledBy, reason = 'rider_cancelled') => {
    try {
        const response = await axios.post(
            `${BACKEND_URL}/api/rides/${rideId}/cancel`,
            null,
            { params: { cancelled_by: cancelledBy, reason } }
        );
        return { success: true, data: response.data.ride, message: response.data.message };
    } catch (error) {
        console.error('Cancel ride error:', error);
        return { success: false, message: error.response?.data?.detail || 'Failed to cancel ride' };
    }
};

/**
 * Reschedule a scheduled ride to a new pickup time
 */
export const rescheduleRide = async (rideId, scheduledTime, riderId) => {
    try {
        const response = await axios.patch(
            `${BACKEND_URL}/api/rides/${rideId}/reschedule`,
            { scheduled_time: scheduledTime, rider_id: riderId }
        );
        return { success: true, data: response.data.ride, message: response.data.message };
    } catch (error) {
        console.error('Reschedule ride error:', error);
        return { success: false, message: error.response?.data?.detail || 'Failed to reschedule ride' };
    }
};


/**
 * Get ride history for a rider
 */
export const getRiderRides = async (riderId, limit = 50, getAllStatuses = false) => {
    try {
        let url = `${BACKEND_URL}/api/rides/rider/${riderId}?limit=${limit}`;
        
        // If we want all statuses (including scheduled), don't filter by status
        if (getAllStatuses) {
            // Fetch multiple status types
            const allRides = [];
            const statuses = ['completed', 'cancelled', 'expired', 'pending', 'accepted', 'scheduled'];
            
            for (const status of statuses) {
                const response = await axios.get(`${BACKEND_URL}/api/rides/rider/${riderId}?status=${status}&limit=${limit}`);
                if (response.data.rides) {
                    allRides.push(...response.data.rides);
                }
            }
            
            return {
                success: true,
                data: allRides
            };
        }
        
        const response = await axios.get(url);

        return {
            success: true,
            data: response.data.rides,
            rides: response.data.rides
        };
    } catch (error) {
        console.error('Get rider rides error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || 'Failed to fetch ride history'
        };
    }
};

/**
 * Get available rides for drivers (with location filtering)
 */
export const getAvailableRides = async (vehicleType = null, driverLocation = null) => {
    try {
        let url = `${BACKEND_URL}/api/rides/available`;
        const params = new URLSearchParams();
        
        if (vehicleType) {
            params.append('vehicle_type', vehicleType);
        }
        
        if (driverLocation && driverLocation.latitude && driverLocation.longitude) {
            params.append('driver_lat', driverLocation.latitude);
            params.append('driver_lon', driverLocation.longitude);
        }
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        const response = await axios.get(url);

        return {
            success: true,
            data: response.data.rides
        };
    } catch (error) {
        console.error('Get available rides error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || 'Failed to fetch available rides'
        };
    }
};

/**
 * Get driver rides history
 */
export const getDriverRides = async (driverId, limit = 50) => {
    try {
        const response = await axios.get(
            `${BACKEND_URL}/api/rides/driver/${driverId}?limit=${limit}`
        );

        return {
            success: true,
            data: response.data.rides
        };
    } catch (error) {
        console.error('Get driver rides error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || 'Failed to fetch ride history'
        };
    }
};

/**
 * Accept a ride (driver action) with optional price adjustment and reason
 */
export const acceptRide = async (rideId, driverId, adjustedPrice = null, adjustmentReason = null) => {
    try {
        const requestBody = {
            driver_id: driverId
        };
        
        if (adjustedPrice !== null) {
            requestBody.adjusted_price = adjustedPrice;
        }
        if (adjustmentReason) {
            requestBody.adjustment_reason = adjustmentReason;
        }

        const response = await axios.post(
            `${BACKEND_URL}/api/rides/${rideId}/accept`,
            requestBody
        );

        return {
            success: true,
            data: response.data.ride,
            message: response.data.message
        };
    } catch (error) {
        console.error('Accept ride error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || 'Failed to accept ride'
        };
    }
};

/**
 * Make a counter offer (rider action)
 */
export const makeCounterOffer = async (rideId, riderId, counterPrice, counterReason = null) => {
    try {
        const params = new URLSearchParams({ 
            rider_id: riderId,
            counter_price: counterPrice
        });
        if (counterReason) {
            params.append('counter_reason', counterReason);
        }

        const response = await axios.post(
            `${BACKEND_URL}/api/rides/${rideId}/counter-offer?${params.toString()}`
        );

        return {
            success: true,
            data: response.data.ride,
            message: response.data.message
        };
    } catch (error) {
        console.error('Counter offer error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || 'Failed to send counter offer'
        };
    }
};

/**
 * Accept counter offer (driver action)
 */
export const acceptCounterOffer = async (rideId, driverId) => {
    try {
        const response = await axios.post(
            `${BACKEND_URL}/api/rides/${rideId}/accept-counter?driver_id=${driverId}`
        );

        return {
            success: true,
            data: response.data.ride,
            message: response.data.message
        };
    } catch (error) {
        console.error('Accept counter offer error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || 'Failed to accept counter offer'
        };
    }
};

/**
 * Accept adjusted price (rider action)
 */
export const acceptAdjustedPrice = async (rideId, riderId) => {
    try {
        const response = await axios.post(
            `${BACKEND_URL}/api/rides/${rideId}/accept-price?rider_id=${riderId}`
        );

        return {
            success: true,
            data: response.data.ride,
            message: response.data.message
        };
    } catch (error) {
        console.error('Accept price error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || 'Failed to accept price'
        };
    }
};

/**
 * Reject adjusted price (rider action)
 */
export const rejectAdjustedPrice = async (rideId, riderId) => {
    try {
        const response = await axios.post(
            `${BACKEND_URL}/api/rides/${rideId}/reject-price?rider_id=${riderId}`
        );

        return {
            success: true,
            data: response.data.ride,
            message: response.data.message
        };
    } catch (error) {
        console.error('Reject price error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || 'Failed to reject price'
        };
    }
};

/**
 * Start a ride (driver action)
 */
export const startRide = async (rideId, confirmationCode) => {
    try {
        const response = await axios.post(
            `${BACKEND_URL}/api/rides/${rideId}/start`,
            { confirmation_code: confirmationCode }
        );

        return {
            success: true,
            data: response.data.ride,
            message: response.data.message
        };
    } catch (error) {
        console.error('Start ride error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || 'Failed to start ride'
        };
    }
};

/**
 * Complete a ride (driver action)
 */
export const completeRide = async (rideId, finalPrice) => {
    try {
        const response = await axios.post(
            `${BACKEND_URL}/api/rides/${rideId}/complete`,
            { final_price: finalPrice }
        );

        return {
            success: true,
            data: response.data.ride,
            message: response.data.message
        };
    } catch (error) {
        console.error('Complete ride error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || 'Failed to complete ride'
        };
    }
};

/**
 * Rate a ride (rider action)
 */
export const rateRide = async (rideId, ratingData) => {
    try {
        const response = await axios.post(
            `${BACKEND_URL}/api/rides/${rideId}/rate`,
            ratingData
        );

        return {
            success: true,
            data: response.data.ride,
            message: response.data.message
        };
    } catch (error) {
        console.error('Rate ride error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || 'Failed to rate ride'
        };
    }
};

/**
 * Calculate estimated price using backend pricing API
 */
export const calculateEstimatedPrice = async (pickupLocation, dropoffLocation, vehicleType) => {
    try {
        // Call backend pricing estimate endpoint
        const response = await axios.post(
            `${BACKEND_URL}/api/pricing/estimate`,
            {
                pickup_location: {
                    lat: pickupLocation.lat,
                    lng: pickupLocation.lng
                },
                dropoff_location: {
                    lat: dropoffLocation.lat,
                    lng: dropoffLocation.lng
                },
                vehicle_type: vehicleType
            }
        );

        return {
            distance: response.data.distance,
            estimatedPrice: response.data.estimated_price,
            eta: response.data.eta,
            breakdown: response.data.breakdown // Optional: price breakdown details
        };
    } catch (error) {
        console.error('Price estimation error:', error);
        
        // Fallback to simple calculation if API fails
        const distance = Math.sqrt(
            Math.pow(dropoffLocation.lat - pickupLocation.lat, 2) + 
            Math.pow(dropoffLocation.lng - pickupLocation.lng, 2)
        ) * 111; // Rough km conversion
        
        // Fallback minimum fares
        const fallbackMinFares = {
            'economy': 5000,
            'premium': 7000,
            'bike': 2000
        };
        
        return {
            distance: Math.round(distance * 10) / 10,
            estimatedPrice: fallbackMinFares[vehicleType] || fallbackMinFares.economy,
            eta: Math.ceil(distance / 0.5),
            error: 'Using fallback pricing - could not connect to pricing service'
        };
    }
};

/**
 * Get available drivers for riders (with location filtering)
 */
export const getAvailableDrivers = async (vehicleType, riderLocation) => {
    try {
        if (!riderLocation || !riderLocation.latitude || !riderLocation.longitude) {
            throw new Error('Rider location is required');
        }
        
        const params = new URLSearchParams({
            vehicle_type: vehicleType,
            rider_lat: riderLocation.latitude,
            rider_lon: riderLocation.longitude
        });

        const response = await axios.get(
            `${BACKEND_URL}/api/rides/available-drivers?${params.toString()}`
        );

        return {
            success: true,
            data: response.data.drivers,
            count: response.data.count,
            searchRadius: response.data.search_radius_km
        };
    } catch (error) {
        console.error('Get available drivers error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || error.message || 'Failed to fetch available drivers'
        };
    }
};

/**
 * Update driver's current location
 */
export const updateDriverLocation = async (driverId, latitude, longitude) => {
    try {
        const response = await axios.post(
            `${BACKEND_URL}/api/rides/driver/${driverId}/update-location?latitude=${latitude}&longitude=${longitude}`
        );

        return {
            success: true,
            data: response.data.location,
            message: response.data.message
        };
    } catch (error) {
        console.error('Update driver location error:', error);
        return {
            success: false,
            message: error.response?.data?.detail || 'Failed to update location'
        };
    }
};

/**
 * Format ride for display in history
 */
export const formatRideForDisplay = (ride) => {
    const formatDate = (isoString) => {
        if (!isoString) return 'N/A';
        const date = new Date(isoString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            'pending': 'bg-yellow-100 text-yellow-700',
            'accepted': 'bg-blue-100 text-blue-700',
            'in_progress': 'bg-purple-100 text-purple-700',
            'completed': 'bg-green-100 text-green-700',
            'cancelled': 'bg-red-100 text-red-700',
            'negotiation_pending': 'bg-orange-100 text-orange-700'
        };
        return colors[status] || 'bg-gray-100 text-gray-700';
    };

    const getStatusText = (status) => {
        const texts = {
            'pending': 'Pending',
            'accepted': 'Accepted',
            'in_progress': 'In Progress',
            'completed': 'Completed',
            'cancelled': 'Cancelled',
            'negotiation_pending': 'Negotiating'
        };
        return texts[status] || status;
    };

    return {
        id: ride.id,
        from: ride.pickup_location?.address || 'Unknown pickup',
        to: ride.dropoff_location?.address || 'Unknown destination',
        date: formatDate(ride.created_at),
        completedDate: formatDate(ride.completed_at),
        amount: ride.final_price || ride.estimated_price || 0,
        rideType: ride.vehicle_type || 'Economy',
        status: ride.status,
        statusText: getStatusText(ride.status),
        statusColor: getStatusColor(ride.status),
        paymentStatus: ride.payment_status,
        rating: ride.rating,
        review: ride.rider_review,
        driverId: ride.driver_id,
        serviceCharge: ride.service_charge,
        driverEarnings: ride.driver_earnings
    };
};
